import { ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate, useSearchParams } from 'react-router';
import { VirtuosoGrid } from 'react-virtuoso';

import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import { NEW_LINE, PATHS, SortByFunctionType } from '@/shared/config';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { getName, parseDateAsLocal } from '@/shared/lib';
import { Skeleton } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import { TableWithInfiniteScroll } from '@/widgets/TableWithInfiniteScroll';

import { useGetPatientsColumnsScheme } from '../../hooks/useGetPatientsColumnsScheme';
import { EmptyResult } from '../EmptyResult/EmptyResult';
import { ListLoadingFailed } from '../ListLoadingFailed/ListLoadingFailed';
import { PatientListCard } from '../PatientListCard/PatientListCard';

import styles from './PatientList.module.scss';

type PatientListProps = {
  className?: string;
  reFetchPatients: (id: string) => void;
  handleAddPatient: () => void;
  sortBy: SortByFunctionType;
  onDoctorFilterChange: (doctorsIDs: string[]) => void;
  onStudyTypesFilterChange: (
    studyTypes: string[],
    hasTranscriptor: boolean | undefined,
  ) => void;
  onAgeGroupFilterChange: (ageGroup: string[]) => void;
  onLastModifiedFilterChange: (dates: { from?: Date; to?: Date }) => void;
  /** Overrides the row target URL (used to keep navigation inside shells). */
};

export const PatientList: FC<PatientListProps> = (props) => {
  const {
    className,
    reFetchPatients,
    handleAddPatient,
    sortBy,
    onDoctorFilterChange,
    onStudyTypesFilterChange,
    onAgeGroupFilterChange,
    onLastModifiedFilterChange,
  } = props;

  const { totalOrganizationPatientCount } = useAppSelector(
    patientModel.selectors.selectCounters,
  );

  const { isFDA } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const { formatMessage, formatDate } = useIntl();

  const navigate = useNavigate();

  const getPatientUrl = (patientID: string) => {
    return generatePath(PATHS.patientProfile_DEPRECATED, { patientID });
  };

  const rowUrl = (patientID: string) => getPatientUrl(patientID);

  const navigateToPatientProfile = (patientID: string) => {
    navigate(rowUrl(patientID));
  };

  const columnsScheme = useGetPatientsColumnsScheme();
  const [searchParams] = useSearchParams();

  const search = searchParams.get('search');

  const { isPhone } = useMedia();

  const { view } = useAppSelector(userModel.selectors.selectSettings);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const patientList = useAppSelector(patientModel.selectors.selectAll);

  const patientListLoading = useAppSelector(
    patientModel.selectors.selectLoading,
  );

  const isRowSkeleton = view === 'row' && !isPhone;

  const isGridView = view === 'grid';

  const showSkeleton =
    (patientListLoading === 'pending' || patientListLoading === 'idle') &&
    patientList.length === 0 &&
    totalOrganizationPatientCount > 0;

  const showEmpty =
    (patientListLoading === 'succeeded' ||
      totalOrganizationPatientCount === 0) &&
    patientList.length === 0 &&
    isEmpty(search);

  const showNothingFound =
    patientListLoading === 'succeeded' &&
    patientList.length === 0 &&
    Boolean(search);

  const patientListGrid = (index: number) => {
    const patient = patientList[index];
    const patientName = getName(
      patient.PersonalData?.FirstName,
      patient.PersonalData?.LastName,
      { userLocale },
    );

    const lastModified = patient.Revision?.UpdatedAt
      ? formatDate(patient.Revision.UpdatedAt.toDate(), {
          dateStyle: 'long',
        })
      : undefined;

    return (
      <PatientListCard
        disableStudies={isFDA}
        key={patient?.ID}
        patientID={patient?.ID}
        patientName={patientName}
        patientBirthDate={
          patient.PersonalData?.DateOfBirth
            ? formatDate(parseDateAsLocal(patient.PersonalData.DateOfBirth), {
                dateStyle: 'long',
              })
            : ''
        }
        patientLastModified={lastModified}
        displayAssetID={patient.DisplayAssetID}
      />
    );
  };

  const loadMore = () => {
    const patient = patientList[patientList.length - 1];
    reFetchPatients(patient.ID);
  };

  return (
    <div
      data-component="patient-list"
      className={cn(
        styles.container,
        isGridView && styles.transparent,
        className,
      )}
    >
      {showSkeleton && !isRowSkeleton && <Skeleton.Grid />}

      {patientListLoading === 'failed' && <ListLoadingFailed />}

      {/* TODO: [1/l] showNothingFound should be displayed only in grid cases, because showNothingFound also is displayed in the TableWithInfinityScroll component */}
      {showNothingFound && isGridView && (
        <EmptyResult
          emptyText={formatMessage(
            {
              id: 'patientList.nothingFound',
              defaultMessage: `It looks like there are no patients with these search parameters.{newLine}Try to change search or filtering parameters`,
            },
            { newLine: NEW_LINE },
          )}
        />
      )}

      {view === 'row' && !isPhone && patientListLoading !== 'failed' ? (
        <TableWithInfiniteScroll
          listData={patientList}
          reFetchList={reFetchPatients}
          itemCounter={totalOrganizationPatientCount}
          clickOnRow={navigateToPatientProfile}
          getRowUrl={rowUrl}
          columnsScheme={columnsScheme as ColumnDef<Patient>[]}
          sortBy={sortBy}
          loadingState="patient"
          showSkeleton={showSkeleton && isRowSkeleton}
          showNothingFound={showNothingFound}
          className={styles.table}
          serverSideSorting
          onDoctorFilterChange={onDoctorFilterChange}
          onStudyTypesFilterChange={onStudyTypesFilterChange}
          onAgeGroupFilterChange={onAgeGroupFilterChange}
          onLastModifiedFilterChange={onLastModifiedFilterChange}
          showEmpty={showEmpty}
          emptyComponent={
            <EmptyResult
              handleButtonClick={handleAddPatient}
              emptyText={formatMessage({
                id: 'patientList.emptyAll',
                defaultMessage: 'There are no patients yet',
              })}
            />
          }
        />
      ) : (
        patientListLoading !== 'failed' &&
        !showNothingFound &&
        (showEmpty ? (
          <EmptyResult
            handleButtonClick={handleAddPatient}
            emptyText={formatMessage({
              id: 'patientList.emptyAll',
              defaultMessage: 'There are no patients yet',
            })}
          />
        ) : (
          <VirtuosoGrid
            listClassName={styles.grid}
            itemClassName={styles.gridItem}
            className={styles.virtuosoGrid}
            totalCount={patientList.length}
            style={{ flex: 1 }}
            endReached={loadMore}
            overscan={1200}
            itemContent={patientListGrid}
          />
        ))
      )}
    </div>
  );
};
